import { useState, useEffect, useRef } from 'react';
import { useCountUp } from '@/hooks/useCountUp';
import { useLiveData } from '@/hooks/useLiveData';

/* ── Live Metric Strip ── */
function LiveMetricStrip({ metrics }: { metrics: { label: string; value: string; live?: { initial: number; range: number; suffix?: string; decimals?: number } }[] }) {
  return (
    <div className="flex flex-wrap gap-3">
      {metrics.map((m) => (
        <LiveMetricBadge key={m.label} label={m.label} value={m.value} live={m.live} />
      ))}
    </div>
  );
}

function LiveMetricBadge({ label, value, live }: {
  label: string;
  value: string;
  live?: { initial: number; range: number; suffix?: string; decimals?: number };
}) {
  const { formatted, flash } = useLiveData({
    initial: live?.initial ?? 0,
    range: live?.range ?? 1,
    decimals: live?.decimals ?? 0,
    interval: 2800,
    suffix: live?.suffix ?? '',
  });

  if (!live) {
    return (
      <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-background-200/60 border border-background-300/30 text-[11px] mono-data text-accent-400 font-semibold">
        {value}
      </span>
    );
  }

  return (
    <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md bg-background-200/60 border border-background-300/30 text-[11px] mono-data text-accent-400 font-semibold transition-all duration-300 ${flash ? 'border-accent-500/40 data-tick' : ''}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
      {label}: {formatted}
    </span>
  );
}

/* ── Section Data Panel ── */
function SectionDataPanel({ data, accentColor }: {
  data: { label: string; countTarget: number; suffix: string; trend: number[]; liveVal: number; liveRange: number };
  accentColor: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.4 });
    if (panelRef.current) obs.observe(panelRef.current);
    return () => obs.disconnect();
  }, []);

  const { formatted } = useCountUp({ target: data.countTarget, duration: 2000, suffix: data.suffix, enabled: visible });
  const { formatted: liveFormatted, flash } = useLiveData({ initial: data.liveVal, range: data.liveRange, decimals: 1, interval: 3000 });

  const maxV = Math.max(...data.trend);
  const w = 200, h = 36;
  const pathD = data.trend.map((v, i) => {
    const x = (i / (data.trend.length - 1)) * w;
    const y = h - (v / maxV) * (h - 2);
    return `${i === 0 ? 'M' : 'L'}${x} ${y}`;
  }).join(' ');

  return (
    <div ref={panelRef} className="terminal-module p-4 md:p-5 mt-6">
      {/* Mini title bar */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full bg-red-400/40"></span>
          <span className="w-2 h-2 rounded-full bg-accent-400/40"></span>
          <span className="w-2 h-2 rounded-full bg-emerald-400/40"></span>
        </div>
        <span className="text-[9px] mono-data text-foreground-600 tracking-wider">LIVE_METRICS :: {data.label}</span>
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {/* Big number */}
        <div className="text-center">
          <span className="block text-[9px] mono-data text-foreground-600 tracking-wide uppercase mb-1">TOTAL</span>
          <span className="block text-2xl md:text-3xl mono-data font-bold text-foreground-100 tabular-nums">
            {visible ? formatted : '--'}
          </span>
        </div>
        {/* Live value */}
        <div className="text-center">
          <span className="block text-[9px] mono-data text-foreground-600 tracking-wide uppercase mb-1">实时</span>
          <span className={`block text-2xl md:text-3xl mono-data font-bold tabular-nums transition-all duration-300 ${flash ? 'data-tick text-accent-400' : 'text-emerald-400'}`}>
            {liveFormatted}
          </span>
        </div>
        {/* Sparkline */}
        <div className="col-span-1 md:col-span-2">
          <span className="block text-[9px] mono-data text-foreground-600 tracking-wide uppercase mb-1">TREND · 20D</span>
          <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-9" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id={`bp-grad-${data.label}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={accentColor} stopOpacity="0.2" />
                <stop offset="100%" stopColor={accentColor} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={`${pathD} L${w} ${h} L0 ${h} Z`} fill={`url(#bp-grad-${data.label})`} />
            <path d={pathD} fill="none" stroke={accentColor} strokeWidth="1.3" strokeOpacity="0.8" className="sparkline-path" />
          </svg>
        </div>
      </div>
    </div>
  );
}

const businesses = [
  {
    id: 'quant',
    title: '蚂蚁量化',
    subtitle: 'Quantitative Research & AI Risk Models',
    icon: 'ri-line-chart-line',
    description: '以人工智能与大数据技术为核心驱动，构建多因子量化选股、CTA趋势跟踪、统计套利与高频交易等多元策略体系。自主研发智能风控引擎，实现策略全生命周期的风险监控与动态优化。',
    features: ['多因子量化选股模型', 'CTA趋势跟踪策略', '统计套利与市场中性', 'AI驱动的智能风控引擎', '全自动交易执行系统'],
    metrics: [
      { label: '策略模型', value: '50+', live: { initial: 52.8, range: 2, decimals: 1 } },
      { label: '风控延迟', value: 'T+0', live: { initial: 3.8, range: 0.3, decimals: 1, suffix: 'ms' } },
      { label: '日均信号', value: '1000+', live: { initial: 1287, range: 40, decimals: 0 } },
    ],
    dataPanel: { label: 'QUANT', countTarget: 50, suffix: '+', trend: [32,35,38,36,40,42,39,44,46,43,48,50,47,52,54,51,55,53,57,56], liveVal: 52.8, liveRange: 2 },
    image: 'https://readdy.ai/api/search-image?query=Abstract%20quantitative%20trading%20visualization%20with%20glowing%20algorithmic%20charts%20and%20data%20streams%2C%20dark%20navy%20background%2C%20blue%20and%20gold%20geometric%20patterns%20crossing%2C%20futuristic%20fintech%20aesthetic%2C%20professional%20dark%20tone%2C%20no%20text&width=800&height=560&seq=biz-quant-det-dark-01&orientation=landscape',
    accent: 'border-l-primary-500',
    panelColor: '#48D1B0',
  },
  {
    id: 'fund',
    title: '蚂蚁基金',
    subtitle: 'Fund Research & Asset Management',
    icon: 'ri-funds-line',
    description: '深入研究全球基金市场，构建科学的基金评价与筛选体系。基于宏观周期分析与风险预算模型，为机构投资者与高净值客户提供跨资产类别、跨区域的定制化资产管理方案，致力于实现长期稳健的投资回报。',
    features: ['全球基金研究平台', '多维基金评价体系', '宏观周期分析框架', '风险预算资产配置', '组合再平衡机制'],
    metrics: [
      { label: '覆盖基金', value: '200+', live: { initial: 215.6, range: 5, decimals: 1 } },
      { label: '评级维度', value: '多维度', live: { initial: 82.4, range: 3, decimals: 1 } },
      { label: '再平衡周期', value: '季度', live: { initial: 91.2, range: 2, decimals: 0, suffix: 'd' } },
    ],
    dataPanel: { label: 'FUND', countTarget: 200, suffix: '+', trend: [140,145,150,148,155,160,158,165,170,168,175,180,178,185,190,188,195,200,198,205], liveVal: 215.6, liveRange: 5 },
    image: 'https://readdy.ai/api/search-image?query=Professional%20fund%20management%20visualization%20with%20diversified%20portfolio%20charts%2C%20dark%20navy%20background%2C%20geometric%20asset%20allocation%20diagrams%2C%20golden%20accent%20lines%2C%20clean%20sophisticated%20dark%20corporate%20style%2C%20no%20text&width=800&height=560&seq=biz-fund-det-dark-02&orientation=landscape',
    accent: 'border-l-accent-500',
    panelColor: '#C8A840',
  },
  {
    id: 'family-office',
    title: '蚂蚁家办',
    subtitle: 'Family Asset Management & Wealth Succession',
    icon: 'ri-building-2-line',
    description: '为超高净值家族提供一站式资产管理服务，涵盖全球资产配置、跨境税务筹划、家族信托架构设计、慈善规划与代际传承方案。以家族长期利益为核心，建立跨越世代与市场周期的稳健资产管理机制。',
    features: ['全球化资产配置', '跨境税务筹划', '家族信托架构设计', '慈善与影响力投资', '代际传承规划'],
    metrics: [
      { label: '服务方案', value: '定制化', live: { initial: 38.2, range: 1.5, decimals: 1 } },
      { label: '跨境架构', value: '多辖区', live: { initial: 6.3, range: 0.2, decimals: 1 } },
      { label: '客户满意度', value: '高', live: { initial: 97.8, range: 0.5, decimals: 1, suffix: '%' } },
    ],
    dataPanel: { label: 'FAMILY', countTarget: 50, suffix: '+', trend: [28,32,35,34,38,40,37,42,45,43,46,48,45,50,52,49,53,51,54,53], liveVal: 38.2, liveRange: 1.5 },
    image: 'https://readdy.ai/api/search-image?query=Elegant%20family%20office%20wealth%20succession%20visualization%2C%20dark%20navy%20background%2C%20golden%20heritage%20symbolism%2C%20interconnected%20legacy%20nodes%2C%20classical%20modern%20fusion%2C%20sophisticated%20trustworthy%20dark%20atmosphere%2C%20no%20text&width=800&height=560&seq=biz-family-det-dark-03&orientation=landscape',
    accent: 'border-l-accent-500',
    panelColor: '#C8A840',
  },
  {
    id: 'capital',
    title: '蚂蚁资本',
    subtitle: 'Private Equity & Alternative Asset Management',
    icon: 'ri-rocket-line',
    description: '聚焦金融科技、人工智能、区块链等新兴赛道，提供从种子轮到Pre-IPO的全阶段股权投资与另类资产管理服务。同时提供并购顾问与战略咨询，通过主动管理与深度赋能实现资产长期增值。',
    features: ['全阶段股权投资', '并购交易顾问', '战略咨询与孵化', '跨境资本运作', '投后管理与赋能'],
    metrics: [
      { label: '投后项目', value: '30+', live: { initial: 34.7, range: 1, decimals: 1 } },
      { label: '退出案例', value: '15+', live: { initial: 16.2, range: 0.5, decimals: 1 } },
      { label: '团队规模', value: '100+', live: { initial: 108.4, range: 3, decimals: 0 } },
    ],
    dataPanel: { label: 'CAPITAL', countTarget: 30, suffix: '+', trend: [15,18,20,19,22,25,23,26,28,27,30,31,29,33,34,32,35,34,36,35], liveVal: 34.7, liveRange: 1 },
    image: 'https://readdy.ai/api/search-image?query=Abstract%20venture%20capital%20and%20innovation%20ecosystem%2C%20dark%20navy%20background%2C%20interconnected%20growth%20nodes%20with%20rising%20silver%20and%20gold%20shapes%2C%20modern%20fintech%20investment%20dark%20style%2C%20no%20text&width=800&height=560&seq=biz-capital-det-dark-04&orientation=landscape',
    accent: 'border-l-primary-500',
    panelColor: '#60A8E0',
  },
];

export default function Business() {
  return (
    <div className="min-h-screen bg-background-50">
      {/* Page Header */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-20 bg-background-50 overflow-hidden">
        <div className="absolute inset-0 bg-dot-matrix opacity-25"></div>
        <div className="absolute top-[20%] right-[15%] w-3 h-3 rounded-full bg-accent-400/40 animate-pulse-glow"></div>
        <div className="absolute top-[50%] right-[35%] w-2 h-2 rounded-full bg-primary-400/40 animate-pulse-glow" style={{ animationDelay: '0.8s' }}></div>
        <div className="relative px-6 md:px-12 lg:px-20">
          <div className="max-w-[1400px] mx-auto">
            <p className="text-accent-500 text-xs md:text-sm font-semibold tracking-[0.25em] uppercase mb-4">Core Business</p>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground-100 leading-tight">核心业务</h1>
            <p className="mt-4 text-sm md:text-base text-foreground-400 max-w-2xl leading-relaxed font-light">
              以量化科技为核心驱动，深耕基金管理、家族办公室与资本投资三大资产管理板块，构建覆盖投资研究、资产配置、财富管理与资本运作的科技资管生态
            </p>
          </div>
        </div>
      </section>

      {/* Business Live Dashboard */}
      <BusinessLiveDashboard />

      {/* Business Sections */}
      {businesses.map((biz, idx) => (
        <section key={biz.id} className={`py-20 md:py-28 ${idx % 2 === 0 ? 'bg-background-100' : 'bg-background-50'}`}>
          <div className="px-6 md:px-12 lg:px-20">
            <div className="max-w-[1400px] mx-auto">
              <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center`}>
                {/* Text side */}
                <div className={idx % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-lg bg-primary-500/10 border border-primary-500/15 flex items-center justify-center">
                      <i className={`${biz.icon} text-lg text-primary-400`}></i>
                    </div>
                    <span className="text-xs font-semibold tracking-wider text-accent-500 uppercase">{biz.subtitle}</span>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/60 animate-pulse-glow ml-auto"></span>
                  </div>
                  <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-100 mb-5">{biz.title}</h2>
                  <p className="text-sm md:text-base text-foreground-400 leading-relaxed mb-6 font-light">{biz.description}</p>

                  {/* Features */}
                  <ul className="space-y-3 mb-6">
                    {biz.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm text-foreground-400">
                        <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0"></i>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Data metrics strip with live data */}
                  <LiveMetricStrip metrics={biz.metrics} />

                  {/* Dynamic data panel */}
                  <SectionDataPanel data={biz.dataPanel} accentColor={biz.panelColor} />
                </div>

                {/* Image side */}
                <div className={idx % 2 === 1 ? 'lg:order-1' : ''}>
                  <div className="relative">
                    <div className="rounded-xl overflow-hidden border border-background-300/30">
                      <img
                        src={biz.image}
                        alt={biz.title}
                        className="w-full rounded-xl"
                      />
                    </div>
                    {/* Accent corner */}
                    <div className={`absolute -top-3 -left-3 w-16 h-16 border-t-2 border-l-2 ${biz.accent} rounded-tl-xl opacity-40`}></div>
                    <div className="absolute -bottom-3 -right-3 w-3 h-3 rounded-full bg-accent-400/50 animate-pulse-glow"></div>

                    {/* Floating LIVE badge */}
                    <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background-100/70 backdrop-blur-md border border-background-300/30">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow"></span>
                      <span className="text-[9px] mono-data text-foreground-400 tracking-wide">LIVE</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      ))}
    </div>
  );
}

/* ── Business Live Dashboard ── */
function BusinessLiveDashboard() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.5 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const metrics = [
    { label: '管理资产', target: 2.8, suffix: 'B+', prefix: '$', live: false },
    { label: '活跃客户', target: 268, suffix: '+', live: true, liveVal: 271.4, liveRange: 3 },
    { label: '覆盖市场', target: 15, suffix: '', live: false },
    { label: '策略上线', target: 52, suffix: '+', live: true, liveVal: 54.2, liveRange: 1.5 },
    { label: '合规通过率', target: 100, suffix: '%', live: false },
    { label: '客户满意度', target: 97.8, suffix: '%', live: true, liveVal: 97.8, liveRange: 0.3, decimals: 1 },
  ];

  return (
    <section ref={ref} className="relative py-8 md:py-10 bg-background-100 border-y border-background-300/20">
      <div className="px-6 md:px-12 lg:px-20">
        <div className="max-w-[1400px] mx-auto">
          <div className="flex items-center gap-3 mb-5">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-400/50"></span>
              <span className="w-2 h-2 rounded-full bg-accent-400/50"></span>
              <span className="w-2 h-2 rounded-full bg-emerald-400/50"></span>
            </div>
            <span className="mono-data text-[10px] text-foreground-600 tracking-[0.15em]">BUSINESS_DASHBOARD :: LIVE v3.2</span>
            <span className="w-px h-3 bg-background-400/30"></span>
            <span className="text-[10px] text-emerald-400 font-semibold mono-data animate-blink">● LIVE</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {metrics.map((m) => (
              <BizDashMetric key={m.label} metric={m} visible={visible} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function BizDashMetric({ metric, visible }: { metric: { label: string; target: number; suffix: string; prefix?: string; live?: boolean; liveVal?: number; liveRange?: number; decimals?: number }; visible: boolean }) {
  const { formatted } = useCountUp({ target: metric.target, duration: 2000, suffix: metric.suffix, prefix: metric.prefix, decimals: metric.decimals ?? 0, enabled: visible });
  const { formatted: liveFormatted, flash } = useLiveData({
    initial: metric.liveVal ?? 0,
    range: metric.liveRange ?? 1,
    decimals: metric.decimals ?? 0,
    interval: 3500,
  });

  return (
    <div className="terminal-module p-3 md:p-4 text-center">
      <span className="block text-[9px] mono-data text-foreground-600 tracking-wider uppercase mb-1.5">{metric.label}</span>
      <span className={`block text-lg md:text-xl mono-data font-bold ${visible ? 'text-foreground-100' : 'text-foreground-600'} tabular-nums transition-colors duration-300`}>
        {metric.prefix || ''}{visible ? formatted : '--'}
      </span>
      {metric.live && (
        <span className={`block text-[10px] mono-data mt-0.5 transition-all duration-300 ${flash ? 'text-accent-400 data-tick' : 'text-emerald-400/70'}`}>
          RT: {liveFormatted}
        </span>
      )}
    </div>
  );
}